import React, { useEffect, useState } from "react";
import { useForm, Controller, SubmitHandler, useWatch } from "react-hook-form";
import { subYears } from "date-fns";
import {
  Animate,
  Button,
  Input,
  Select,
  DateField,
  ButtonLoading,
  IconButton,
} from "components";

import {
  useAddPatient,
  useEditPatientProfile,
  useGetGroups,
  useCurrentUser,
} from "utils/hooks";
import useOrganizationSettings, {
  getPatientWord,
  getPatientWordCapitalized,
} from "utils/hooks/organizations/useOrganizationSettings";
import { useTranslation } from "react-i18next";
import { Patient, Group } from "types";
import { AddPatientArgs } from "utils/hooks/patients/useAddPatient";
import { EditPatientProfileArgs } from "utils/hooks/patients/useEditPatient";
import "./AddPatientsForm.styles.scss";

interface PrefilledPatientData {
  email?: string;
  firstName?: string;
  lastName?: string;
}
interface AppPatientFormProps {
  onSuccess: () => void;
  onNewPatient?: (arg: any) => void;
  patient?: Patient | null;
  prefilledData?: PrefilledPatientData;
  redirectToNewPatient?: boolean;
}
interface GroupOption {
  value: string;
  label: string;
}

const AddPatientForm = ({
  onSuccess,
  onNewPatient,
  patient,
  prefilledData,
  redirectToNewPatient = true,
}: AppPatientFormProps) => {
  const { t } = useTranslation(["common"]);
  const {
    register,
    handleSubmit,
    errors,
    control,
    setValue,
    formState: { isDirty },
  } = useForm();
  const { submitAddPatient, isSubmitSuccessful, newPatient, isSubmitting } =
    useAddPatient();
  const { updatePatientProfile, isProfileSubmitting, isUpdateSuccessful } =
    useEditPatientProfile({
      patientId: patient?.uuid,
    });
  const { groups } = useGetGroups({});
  const hasGroups = groups && groups?.length > 0;
  const { settings: orgSettings } = useOrganizationSettings();

  // Get autoSendInvites setting from orgSettings
  const autoSendInvitesSetting = orgSettings?.settings?.find(
    (s) => s.settingKey === "autoSendInvites"
  );
  const autoSendInvites = autoSendInvitesSetting
    ? autoSendInvitesSetting.settingValue === true ||
      autoSendInvitesSetting.settingValue === "true"
    : true; // default true

  // Set initial sendInvite state from autoSendInvites
  const [sendInvite, setSendInvite] = useState(autoSendInvites);
  const [emailValue, setEmailValue] = useState(
    prefilledData?.email || patient?.email || ""
  );
  const { user } = useCurrentUser();
  const showSendInviteCheckbox = true;
  // user?.activeOrganizationId === "f914dd22-aa3d-4bce-a4e4-c17e0ed96f83";

  const liftAgeSetting = orgSettings?.settings?.find(
    (s) => s.settingKey === "liftAgeRestriction"
  );
  const liftAgeRestriction = liftAgeSetting
    ? liftAgeSetting.settingValue === true ||
      liftAgeSetting.settingValue === "true"
    : false;

  const mapGroupsToOptions = (groups: Group[]) => {
    return groups?.map((group) => {
      return {
        value: group.uuid,
        label: group.name,
      };
    });
  };

  const filterGroups = (include: boolean) => {
    const filteredGroups = groups?.filter((group) => {
      const isMember = group.groupMembersIds.includes(patient?.uuid ?? "");

      return (include ? isMember : !isMember) && group.status === "ACTIVE";
    });
    return mapGroupsToOptions(filteredGroups) || [];
  };

  const defaultGroups = filterGroups(true);
  const groupOptions = filterGroups(false);

  const genders = [
    { value: "male", label: `${t("patients.male_label")}` },
    { value: "female", label: `${t("patients.female_label")}` },
    { value: "nonbinary", label: `${t("patients.non_binary_label")}` },
    { value: "other", label: `${t("patients.other_gender_label")}` },
  ];

  useEffect(() => {
    if (defaultGroups && defaultGroups.length > 0) {
      setValue("groups", defaultGroups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedGroups: any = useWatch({
    control,
    name: "groups",
  });

  const handleRemoveGroup = (removedGroup: GroupOption) => {
    const updatedGroups = selectedGroups.filter(
      (group: GroupOption) => group.value !== removedGroup.value
    );

    setValue("groups", updatedGroups, { shouldDirty: true });
  };

  useEffect(() => {
    if (isSubmitSuccessful || isUpdateSuccessful) {
      onSuccess();
      onNewPatient && onNewPatient(newPatient);
    }
  }, [
    isSubmitSuccessful,
    isUpdateSuccessful,
    newPatient,
    onNewPatient,
    onSuccess,
  ]);

  // Only use real-time filler email in create mode
  const firstNameRaw = useWatch({ control, name: "first_name" });
  const lastNameRaw = useWatch({ control, name: "last_name" });
  const firstName = typeof firstNameRaw === "string" ? firstNameRaw : "";
  const lastName = typeof lastNameRaw === "string" ? lastNameRaw : "";
  const patientFirst = firstName.replace(/\s+/g, "").toLowerCase();
  const patientLast = lastName.replace(/\s+/g, "").toLowerCase();
  const therapistEmail = user?.email || "therapist@example.com";
  const therapistEmailParts = therapistEmail.split("@");
  const therapistEmailPrefix = therapistEmailParts[0];
  const therapistEmailDomain = therapistEmailParts[1];
  const fillerEmail = `${therapistEmailPrefix}+${patientFirst}_${patientLast}@${therapistEmailDomain}`;

  const emailHint =
    patient && patient.email ? (
      t("patients.email_hint_edit_disabled", {
        patientWord: getPatientWord(orgSettings),
      })
    ) : sendInvite ? (
      t("patients.email_hint_invitation_required", {
        patientWord: getPatientWord(orgSettings),
      })
    ) : (
      <span>
        {t("patients.email_hint_no_invite_prefix", {
          patientWord: getPatientWord(orgSettings),
        })}{" "}
        <button
          type="button"
          onClick={() => setEmailValue(fillerEmail)}
          style={{
            textDecoration: "underline",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
            color: "inherit",
          }}
        >
          {fillerEmail}
        </button>
      </span>
    );

  const currentGender = genders.filter(
    (gender) => gender.value === patient?.profile?.gender
  )[0];

  const currentDob = patient?.profile?.dob;
  const hasPatientDob =
    currentDob !== null &&
    currentDob !== "null" &&
    currentDob !== "undefined" &&
    currentDob !== undefined;

  const getCurrentFormHandler = ():
    | SubmitHandler<AddPatientArgs>
    | SubmitHandler<EditPatientProfileArgs>
    | undefined => {
    if (patient) {
      return (data: AddPatientArgs) => {
        const groups = data.groups || [];

        const newGroups = groups.filter(
          (group) =>
            !defaultGroups.some(
              (defaultGroup) => defaultGroup.value === group.value
            )
        );
        const removedGroups = defaultGroups.filter(
          (defaultGroup) =>
            !groups.some((group) => group.value === defaultGroup.value)
        );

        const updatedData = {
          ...data,
          newGroups,
          removedGroups,
        };

        updatePatientProfile(updatedData);
      };
    }

    // In create mode, include sendInvite in the payload
    return (data: AddPatientArgs) => {
      submitAddPatient({ ...data, sendInvite }, redirectToNewPatient);
    };
  };

  // When orgSettings changes, update sendInvite default
  useEffect(() => {
    setSendInvite(autoSendInvites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendInvites]);

  return (
    <div id="add_patient_form">
      <form
        onSubmit={handleSubmit(
          getCurrentFormHandler() as SubmitHandler<AddPatientArgs>
        )}
        autoComplete="off"
      >
        <Animate animation="fadeInLeft" delay=".25">
          <Input
            name="first_name"
            type="text"
            label={t("common_labels.first_name")}
            placeholder={t("patients.patient_first_name_placeholder", {
              patientWordCapitalized: getPatientWordCapitalized(orgSettings),
            })}
            forwardRef={register({ required: true })}
            error={
              errors.first_name && t("patients.patient_first_name_required")
            }
            defaultValue={
              prefilledData?.firstName || patient?.firstName || undefined
            }
          />
        </Animate>
        <Animate animation="fadeInLeft" delay=".35">
          <Input
            name="last_name"
            type="text"
            label={t("common_labels.last_name")}
            placeholder={t("patients.patient_last_name_placeholder", {
              patientWordCapitalized: getPatientWordCapitalized(orgSettings),
            })}
            forwardRef={register({ required: true })}
            error={errors.last_name && t("patients.patient_last_name_required")}
            defaultValue={
              prefilledData?.lastName || patient?.lastName || undefined
            }
          />
        </Animate>
        <Animate animation="fadeInLeft" delay=".45">
          <Input
            name="email"
            type="email"
            label={t("common_labels.email")}
            placeholder={t("patients.patient_email_placeholder")}
            forwardRef={register({ required: true })}
            error={errors.email && t("patients.patient_email_required")}
            hint={emailHint}
            defaultValue={emailValue}
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            disabled={patient?.email ? true : false}
          />
          {/* Send invitation checkbox, only in create mode and for specific org */}
          {showSendInviteCheckbox && !patient && (
            <div style={{ marginBottom: "20px", marginTop: "-20px" }}>
              <label>
                <input
                  type="checkbox"
                  checked={sendInvite}
                  onChange={(e) => setSendInvite(e.target.checked)}
                />{" "}
                {t(
                  "patients.send_invitation_email_label",
                  "Send invitation email"
                )}
              </label>
            </div>
          )}
        </Animate>
        <Animate
          animation="fadeInLeft"
          delay=".55"
          style={{ position: "relative" }}
        >
          <Controller
            name="dob"
            control={control}
            defaultValue={hasPatientDob ? new Date(currentDob) : null}
            render={({ onChange, value }) => (
              <DateField
                name="dob"
                dateFormat="MM/dd/yyyy"
                label={t("common_labels.dob")}
                placeholder={t("patients.patient_dob_placeholder", {
                  patientWordCapitalized:
                    getPatientWordCapitalized(orgSettings),
                })}
                selected={value}
                onChange={onChange}
                maxDate={subYears(new Date(), liftAgeRestriction ? 0 : 13)}
                showMonthDropdown
                showYearDropdown
              />
            )}
          />
        </Animate>
        <Animate animation="fadeInLeft" delay=".65">
          <Input
            name="idNumber"
            type="text"
            label={t("patients.id_label")}
            hint={t("patients.patient_id_hint")}
            forwardRef={register()}
            defaultValue={patient?.profile?.idNumber ?? undefined}
          />
        </Animate>
        <div>
          <Select
            name="gender"
            label={t("common_labels.gender")}
            placeholder={t("patients.patient_gender_placeholder", {
              patientWordCapitalized: getPatientWordCapitalized(orgSettings),
            })}
            options={genders}
            forwardRef={register()}
            forwardControl={control}
            defaultValue={patient && currentGender}
          />
        </div>
        {hasGroups && (
          <Animate animation="fadeInLeft" delay=".85">
            <div className="group-selector">
              <Select
                name="groups"
                label={t("page_titles.groups_title")}
                options={groupOptions}
                placeholder={t("patients.choose_group_placeholder")}
                forwardControl={control}
                isMulti={true}
                controlShouldRenderValue={false}
                noOptionsMessage={() =>
                  t("patients.patient_added_to_all_groups_message", {
                    patientWord: getPatientWord(orgSettings),
                  })
                }
                className="group-selector__select"
              />
              {selectedGroups && selectedGroups.length > 0 && (
                <div className="group-selector__groups">
                  {selectedGroups.map((group: GroupOption) => (
                    <div key={group.value}>
                      <div className="group-selector__group">
                        {group.label}
                        <IconButton
                          icon="CloseCrossBold"
                          onClick={() => handleRemoveGroup(group)}
                          extraClass="group-selector__remove-btn"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Animate>
        )}
        <Button
          type="submit"
          name="add_patient_button"
          value={
            patient ? (
              t("common_labels.save_button")
            ) : isSubmitting ? (
              <ButtonLoading />
            ) : (
              t("patients.add_patient_button", {
                patientWord: getPatientWord(orgSettings),
                patientWordCapitalized: getPatientWordCapitalized(orgSettings),
              })
            )
          }
          fullWidth
          isLoading={isSubmitting || isProfileSubmitting}
          disabled={!isDirty}
        />
      </form>
    </div>
  );
};

export default AddPatientForm;
